"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.saveDefinition = exports.changedDefinitionsTracker = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * Copyright 2025 Scott Bender (scott@scottbender.net)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const react_1 = require("react");
const reactstrap_1 = require("reactstrap");
const ts_pgns_1 = require("@canboat/ts-pgns");
const canboatjs_1 = require("@canboat/canboatjs");
const SentencePanel_1 = require("./SentencePanel");
const PgnDefinitionTab_1 = require("./PgnDefinitionTab");
const LookupEditor_1 = __importDefault(require("./LookupEditor"));
const BitLookupEditor_1 = __importDefault(require("./BitLookupEditor"));
const PgnDefinitionTab_2 = require("./PgnDefinitionTab");
const rxjs_1 = require("rxjs");
const localStorage_1 = require("../utils/localStorage");
const observable_hooks_1 = require("observable-hooks");
const PGN_DEFINITIONS_TAB = 'pgn-definitions';
const LOOKUPS_TAB = 'lookups';
const BIT_LOOKUPS_TAB = 'bit-lookups';
exports.changedDefinitionsTracker = {
    _originals: {
        definitions: {},
        lookups: {},
        bitLookups: {},
    },
    // Local storage keys
    _storageKeys: {
        definitions: 'changedDefinitionsTracker_definitions',
        lookups: 'changedDefinitionsTracker_lookups',
        bitLookups: 'changedDefinitionsTracker_bitLookups',
    },
    // In-memory cache
    _cache: {
        definitions: null,
        lookups: null,
        bitLookups: null,
    },
    // Load from local storage with error handling
    _loadFromStorage(key, defaultValue) {
        return localStorage_1.localStorage.getItem(key, defaultValue);
    },
    // Save to local storage with error handling
    _saveToStorage(key, value) {
        localStorage_1.localStorage.setItem(key, value);
    },
    // Lazy getters with caching
    get definitions() {
        if (this._cache.definitions === null) {
            this._cache.definitions = this._loadFromStorage(this._storageKeys.definitions, {});
            Object.values(this._cache.definitions).forEach((def) => {
                (0, ts_pgns_1.updatePGN)(def);
            });
        }
        return this._cache.definitions;
    },
    get lookups() {
        if (this._cache.lookups === null) {
            this._cache.lookups = this._loadFromStorage(this._storageKeys.lookups, {});
            Object.values(this._cache.lookups).forEach((enumItem) => {
                (0, ts_pgns_1.updateLookup)(enumItem);
            });
        }
        return this._cache.lookups;
    },
    get bitLookups() {
        if (this._cache.bitLookups === null) {
            this._cache.bitLookups = this._loadFromStorage(this._storageKeys.bitLookups, {});
            Object.values(this._cache.bitLookups).forEach((bitEnumItem) => {
                (0, ts_pgns_1.updateBitLookup)(bitEnumItem);
            });
        }
        return this._cache.bitLookups;
    },
    addDefinition(definition) {
        if (!this.hasDefinition(definition.Id)) {
            const existing = (0, ts_pgns_1.getPGNWithId)(definition.Id);
            if (existing) {
                this._originals.definitions[definition.Id] = existing;
            }
        }
        this.definitions[definition.Id] = definition;
        this._saveToStorage(this._storageKeys.definitions, this.definitions);
        (0, ts_pgns_1.updatePGN)(definition);
        console.log(`Tracked definition change for PGN ID ${definition.Id}. Total tracked: ${Object.keys(this.definitions).length}`);
    },
    hasDefinition(pgnId) {
        return this.definitions.hasOwnProperty(pgnId);
    },
    getDefinition(pgnId) {
        return this.definitions[pgnId];
    },
    clearDefinition(pgnId) {
        if (!this.hasDefinition(pgnId)) {
            console.warn(`Attempted to clear non-existent PGN ID ${pgnId}`);
            return;
        }
        delete this.definitions[pgnId];
        this._saveToStorage(this._storageKeys.definitions, this.definitions);
        if (this._originals.definitions[pgnId]) {
            // Restore original definition if it exists
            (0, ts_pgns_1.updatePGN)(this._originals.definitions[pgnId]);
        }
        else {
            (0, ts_pgns_1.removePGN)(this.definitions[pgnId]);
        }
        console.log(`Removed tracking for PGN ID ${pgnId}. Remaining: ${Object.keys(this.definitions).length}`);
    },
    addLookup(enumeration) {
        if (enumeration.EnumValues) {
            if (!this.hasLookup(enumeration.Name)) {
                const existing = this.getLookup(enumeration.Name);
                if (existing) {
                    this._originals.lookups[enumeration.Name] = existing;
                }
            }
            this.lookups[enumeration.Name] = enumeration;
            this._saveToStorage(this._storageKeys.lookups, this.lookups);
            (0, ts_pgns_1.updateLookup)(enumeration);
            console.log(`Tracked change for lookup ${enumeration.Name}`);
        }
        else {
            if (!this.hasBitLookup(enumeration.Name)) {
                const existing = this.getBitLookup(enumeration.Name);
                if (existing) {
                    this._originals.bitLookups[enumeration.Name] = existing;
                }
            }
            this.bitLookups[enumeration.Name] = enumeration;
            this._saveToStorage(this._storageKeys.bitLookups, this.bitLookups);
            (0, ts_pgns_1.updateBitLookup)(enumeration);
            console.log(`Tracked change for bit lookup ${enumeration.Name}`);
        }
    },
    hasLookup(enumName) {
        return this.lookups.hasOwnProperty(enumName);
    },
    hasBitLookup(enumName) {
        return this.bitLookups.hasOwnProperty(enumName);
    },
    getLookup(enumName) {
        return this.lookups[enumName];
    },
    getBitLookup(enumName) {
        return this.bitLookups[enumName];
    },
    getChangedDefinitions() {
        return new Set(Object.keys(this.definitions));
    },
    getLookups() {
        return Object.values(this.lookups);
    },
    getBitLookups() {
        return Object.values(this.bitLookups);
    },
    getChangedLookups() {
        return {
            lookups: new Set(Object.keys(this.lookups)),
            bitLookups: new Set(Object.keys(this.bitLookups)),
        };
    },
    getAllChanges() {
        return {
            definitions: this.getChangedDefinitions(),
            ...this.getChangedLookups(),
        };
    },
    clearLookup(enumName, type) {
        if (type === 'lookup') {
            if (!this.lookups.hasOwnProperty(enumName)) {
                console.warn(`Attempted to clear non-existent lookup ${enumName}`);
                return;
            }
            (0, ts_pgns_1.removeLookup)(this.lookups[enumName]);
            delete this.lookups[enumName];
            this._saveToStorage(this._storageKeys.lookups, this.lookups);
            if (this._originals.lookups[enumName]) {
                // Restore original lookup if it exists
                (0, ts_pgns_1.updateLookup)(this._originals.lookups[enumName]);
            }
        }
        else {
            if (!this.bitLookups.hasOwnProperty(enumName)) {
                console.warn(`Attempted to clear non-existent bit lookup ${enumName}`);
                return;
            }
            (0, ts_pgns_1.removeBitLookup)(this.bitLookups[enumName]);
            delete this.bitLookups[enumName];
            this._saveToStorage(this._storageKeys.bitLookups, this.bitLookups);
            if (this._originals.bitLookups[enumName]) {
                // Restore original bit lookup if it exists
                (0, ts_pgns_1.updateBitLookup)(this._originals.bitLookups[enumName]);
            }
        }
    },
    clearAllDefinitions() {
        // Clear only definitions
        this._cache.definitions = {};
        this._saveToStorage(this._storageKeys.definitions, {});
        console.log('Cleared all tracked definition changes');
    },
    clearAllLookups() {
        // Clear only lookups
        this._cache.lookups = {};
        this._saveToStorage(this._storageKeys.lookups, {});
        console.log('Cleared all tracked lookup changes');
    },
    clearAllBitLookups() {
        // Clear only bit lookups
        this._cache.bitLookups = {};
        this._saveToStorage(this._storageKeys.bitLookups, {});
        console.log('Cleared all tracked bit lookup changes');
    },
    clearAll() {
        // Clear in-memory cache
        this._cache.definitions = {};
        this._cache.lookups = {};
        this._cache.bitLookups = {};
        // Clear local storage
        this._saveToStorage(this._storageKeys.definitions, {});
        this._saveToStorage(this._storageKeys.lookups, {});
        this._saveToStorage(this._storageKeys.bitLookups, {});
        console.log('Cleared all tracked changes');
    },
};
const fixupFallbackPGN = (newDef, pgnData) => {
    if (newDef.Fallback) {
        const pgnNumber = pgnData?.pgn || newDef.PGN;
        newDef.Fallback = false;
        newDef.Description = `My ${pgnNumber}`;
        newDef.Id = `my${pgnNumber}`;
        newDef.Explanation = undefined;
        if (pgnData) {
            if (newDef.Fields.length > 0 && newDef.Fields[0].Id === 'manufacturerCode') {
                newDef.Fields[0].Match = ts_pgns_1.ManufacturerCodeValues[pgnData.fields.manufacturerCode] || undefined;
                newDef.Description = `${pgnData.fields.manufacturerCode}: ${pgnNumber}`;
                newDef.Id = (0, PgnDefinitionTab_2.toCamelCase)(newDef.Description);
            }
            if (newDef.Fields.length > 2 && newDef.Fields[2].Id === 'industryCode') {
                newDef.Fields[2].Match = ts_pgns_1.IndustryCodeValues[pgnData.fields.industryCode] || undefined;
            }
            const partialMatch = pgnData.partialMatch;
            if (partialMatch) {
                const partial = (0, ts_pgns_1.getPGNWithId)(partialMatch);
                const hasDataField = newDef.Fields[newDef.Fields.length - 1].Id === 'data';
                const start = hasDataField ? newDef.Fields.length - 1 : newDef.Fields.length;
                if (hasDataField) {
                    newDef.Fields = newDef.Fields.slice(0, start);
                }
                for (let i = start; i < partial.Fields.length; i++) {
                    const field = partial.Fields[i];
                    const val = pgnData.fields[field.Id];
                    if (val !== undefined) {
                        const newField = { ...field };
                        if (field.Match !== undefined) {
                            if (field.LookupEnumeration) {
                                if (typeof val === 'string') {
                                    newField.Match = (0, ts_pgns_1.getEnumerationValue)(field.LookupEnumeration, val);
                                }
                                else {
                                    newField.Match = val;
                                }
                            }
                        }
                        newDef.Fields.push(newField);
                    }
                }
            }
        }
    }
};
const saveDefinition = (updatedDefinition, pgnData) => {
    let definition = pgnData?.getDefinition();
    if (pgnData !== undefined) {
        ;
        updatedDefinition.sampleData = pgnData.input;
    }
    if (definition !== undefined && exports.changedDefinitionsTracker.hasDefinition(definition.Id)) {
        if (definition.Id !== updatedDefinition.Id) {
            exports.changedDefinitionsTracker.clearDefinition(definition.Id);
            (0, ts_pgns_1.removePGN)(definition);
        }
        exports.changedDefinitionsTracker.addDefinition(updatedDefinition);
    }
    else {
        const newDef = {
            ...JSON.parse(JSON.stringify(updatedDefinition)),
            PGN: updatedDefinition.PGN,
        };
        fixupFallbackPGN(newDef, pgnData);
        exports.changedDefinitionsTracker.addDefinition(newDef);
        updatedDefinition = newDef;
    }
    return updatedDefinition;
};
exports.saveDefinition = saveDefinition;
const parser = new canboatjs_1.FromPgn({
    returnNulls: true,
    checkForInvalidFields: true,
    useCamel: true,
    useCamelCompat: false,
    returnNonMatches: true,
    createPGNObjects: true,
    includeInputData: true,
    includeRawData: true,
    includeByteMapping: true,
});
parser.on('error', (pgn, error) => {
    console.error(`Error parsing ${pgn.pgn} ${error}`);
    //console.error(error.stack)
});
const EditorTab = ({ isEmbedded = false, deviceInfo }) => {
    const [activeSubTab, setActiveSubTab] = (0, react_1.useState)(PGN_DEFINITIONS_TAB);
    const [selectedPgn] = (0, react_1.useState)(new rxjs_1.ReplaySubject());
    const [selectedPgnWithHistory] = (0, react_1.useState)(new rxjs_1.ReplaySubject());
    const [selectedDefinitionId, setSelectedDefinitionId] = (0, react_1.useState)(null);
    const [trackerVersion, setTrackerVersion] = (0, react_1.useState)(0); // Force re-renders when tracker changes
    // Lookup editing state
    const [editingLookup, setEditingLookup] = (0, react_1.useState)(null);
    const [lookupValues, setLookupValues] = (0, react_1.useState)([]);
    const [newLookupName, setNewLookupName] = (0, react_1.useState)('');
    // Get the current PGN value for PgnDefinitionTab
    const currentPgn = (0, observable_hooks_1.useObservableState)(selectedPgn, undefined);
    const handleSubTabChange = (tabId) => {
        setActiveSubTab(tabId);
    };
    const handleDefinitionSelect = (definitionId, definition) => {
        setSelectedDefinitionId(definitionId);
        const input = definition.sampleData;
        let pgnData = undefined;
        if (input && input.length > 0) {
            for (const line of input) {
                pgnData = parser.parseString(line);
            }
        }
        else {
            pgnData = { getDefinition: () => definition };
        }
        if (pgnData) {
            selectedPgn.next(pgnData);
            selectedPgnWithHistory.next({
                current: pgnData,
                history: [],
            });
        }
        else {
            console.error(`Failed to parse PGN data for definition ID ${definitionId}`);
        }
    };
    const handleDefinitionSave = (definition) => {
        (0, exports.saveDefinition)(definition, currentPgn);
        setTrackerVersion((v) => v + 1); // Force re-render when adding/updating definitions
        handleDefinitionSelect(definition.Id, definition);
    };
    const clearDefinitionSelection = () => {
        setSelectedDefinitionId(null);
        selectedPgnWithHistory.next(null);
    };
    // Helper functions that update the tracker and force re-renders
    const clearAllDefinitions = () => {
        exports.changedDefinitionsTracker.clearAllDefinitions();
        setTrackerVersion((v) => v + 1);
        clearDefinitionSelection();
    };
    const clearSingleDefinition = (id) => {
        exports.changedDefinitionsTracker.clearDefinition(id);
        setTrackerVersion((v) => v + 1);
        if (selectedDefinitionId === id) {
            clearDefinitionSelection();
        }
    };
    // Handler functions for lookups
    const handleLookupChange = (enumeration) => {
        exports.changedDefinitionsTracker.addLookup(enumeration);
        setTrackerVersion((v) => v + 1); // Force re-render
    };
    const handleLookupRemove = (enumName) => {
        exports.changedDefinitionsTracker.clearLookup(enumName, 'lookup');
        setTrackerVersion((v) => v + 1); // Force re-render
    };
    const clearAllLookups = () => {
        // Clear only lookups, not definitions or bit lookups
        exports.changedDefinitionsTracker.clearAllLookups();
        setTrackerVersion((v) => v + 1);
    };
    // Lookup editing functions (similar to PgnDefinitionTab)
    const handleLookupEdit = (enumName, type, lookupValues) => {
        if (!enumName && lookupValues.length === 0) {
            // This is a new lookup creation request without name, initialize with default values
            setLookupValues([
                { key: '0', value: 'Unknown' },
                { key: '1', value: 'Value1' },
                { key: '2', value: 'Value2' },
            ]);
            setNewLookupName('');
            setEditingLookup({ enumName: '', type });
            return;
        }
        // Load existing lookup values or use provided ones
        setLookupValues(lookupValues);
        setNewLookupName(enumName || ''); // Set the existing name for editing
        setEditingLookup({ enumName, type });
    };
    const closeLookupEditor = () => {
        setEditingLookup(null);
        setLookupValues([]);
        setNewLookupName('');
    };
    const addLookupValue = () => {
        setLookupValues((prev) => [...prev, { key: prev.length.toString(), value: `Value${prev.length}` }]);
    };
    const removeLookupValue = (index) => {
        setLookupValues((prev) => prev.filter((_, i) => i !== index));
    };
    const updateLookupValue = (index, key, value) => {
        setLookupValues((prev) => prev.map((item, i) => (i === index ? { key, value } : item)));
    };
    const saveLookupValues = () => {
        if (!editingLookup)
            return;
        // Use the provided enumName or the entered newLookupName
        const enumName = newLookupName.trim();
        if (!enumName) {
            alert('Please enter a name for the lookup');
            return;
        }
        // If this is a rename operation (original name exists and is different from new name)
        const isRename = editingLookup.enumName && editingLookup.enumName !== enumName;
        if (editingLookup.type === 'lookup') {
            // If renaming, first remove the old lookup
            if (isRename) {
                exports.changedDefinitionsTracker.clearLookup(editingLookup.enumName, 'lookup');
            }
            // Create new enumeration
            const newEnumeration = {
                Name: enumName,
                MaxValue: 255, // Default max value
                EnumValues: lookupValues
                    .map((item) => ({
                    Value: parseInt(item.key) || 0,
                    Name: item.value,
                }))
                    .sort((a, b) => a.Value - b.Value),
            };
            handleLookupChange(newEnumeration);
        }
        else if (editingLookup.type === 'bitlookup') {
            // If renaming, first remove the old bit lookup
            if (isRename) {
                exports.changedDefinitionsTracker.clearLookup(editingLookup.enumName, 'bitlookup');
            }
            // Create new bit enumeration
            const newBitEnumeration = {
                Name: enumName,
                MaxValue: 255, // Default max value for bit enumerations
                EnumBitValues: lookupValues
                    .map((item) => ({
                    Bit: parseInt(item.key) || 0,
                    Name: item.value,
                }))
                    .sort((a, b) => a.Bit - b.Bit),
            };
            handleBitLookupChange(newBitEnumeration);
        }
        closeLookupEditor();
    };
    // Handler functions for bit lookups
    const handleBitLookupChange = (enumeration) => {
        exports.changedDefinitionsTracker.addLookup(enumeration);
        setTrackerVersion((v) => v + 1); // Force re-render when adding/updating bit lookups
    };
    const handleBitLookupRemove = (enumName) => {
        exports.changedDefinitionsTracker.clearLookup(enumName, 'bitlookup');
        setTrackerVersion((v) => v + 1);
    };
    const clearAllBitLookups = () => {
        // Clear only bit lookups, not definitions or lookups
        exports.changedDefinitionsTracker.clearAllBitLookups();
        setTrackerVersion((v) => v + 1);
    };
    // Handler for when PgnDefinitionTab saves a lookup
    const handlePgnDefinitionLookupSave = (enumName, lookupType, lookupValues) => {
        // This will be called when PgnDefinitionTab saves lookup changes
        // We need to update our tracker version to force re-render of the editors
        setTrackerVersion((v) => v + 1);
    };
    return ((0, jsx_runtime_1.jsxs)(reactstrap_1.Container, { fluid: true, children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsxs)(reactstrap_1.CardBody, { children: [(0, jsx_runtime_1.jsxs)("div", { className: "mb-3", children: [(0, jsx_runtime_1.jsx)("h4", { children: "PGN Definition Editor" }), (0, jsx_runtime_1.jsx)("p", { className: "text-muted", children: "Edit and manage PGN definitions, Lookups, and BitLookups for NMEA 2000 data interpretation." })] }), (0, jsx_runtime_1.jsxs)(reactstrap_1.Nav, { tabs: true, className: "mb-3", children: [(0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeSubTab === PGN_DEFINITIONS_TAB ? 'active' : '', onClick: () => handleSubTabChange(PGN_DEFINITIONS_TAB), style: { cursor: 'pointer' }, children: "PGN Definitions" }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeSubTab === LOOKUPS_TAB ? 'active' : '', onClick: () => handleSubTabChange(LOOKUPS_TAB), style: { cursor: 'pointer' }, children: "Lookups" }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.NavItem, { children: (0, jsx_runtime_1.jsx)(reactstrap_1.NavLink, { className: activeSubTab === BIT_LOOKUPS_TAB ? 'active' : '', onClick: () => handleSubTabChange(BIT_LOOKUPS_TAB), style: { cursor: 'pointer' }, children: "Bit Lookups" }) })] }), (0, jsx_runtime_1.jsxs)(reactstrap_1.TabContent, { activeTab: activeSubTab, children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.TabPane, { tabId: PGN_DEFINITIONS_TAB, children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Row, { className: "mb-3", children: (0, jsx_runtime_1.jsx)(reactstrap_1.Col, { md: "12", children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsxs)(reactstrap_1.CardBody, { children: [(0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [(0, jsx_runtime_1.jsx)("h5", { className: "mb-0", children: "Changed PGN Definitions" }), (0, jsx_runtime_1.jsx)(reactstrap_1.Button, { color: "secondary", size: "sm", onClick: clearAllDefinitions, disabled: Object.keys(exports.changedDefinitionsTracker.definitions).length === 0, children: "Clear All" })] }), Object.keys(exports.changedDefinitionsTracker.definitions).length === 0 ? ((0, jsx_runtime_1.jsxs)("div", { className: "text-muted text-center p-4", children: [(0, jsx_runtime_1.jsx)("p", { children: "No changed definitions" }), (0, jsx_runtime_1.jsx)("small", { children: "Modified PGN definitions will appear here" })] })) : ((0, jsx_runtime_1.jsx)("div", { style: {
                                                                    width: '100%',
                                                                    height: '200px',
                                                                    overflow: 'auto',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                }, children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Table, { responsive: true, bordered: true, size: "sm", children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("th", { children: "PGN" }), (0, jsx_runtime_1.jsx)("th", { children: "Description" }), (0, jsx_runtime_1.jsx)("th", { style: { width: '50px' } })] }) }), (0, jsx_runtime_1.jsx)("tbody", { children: Object.entries(exports.changedDefinitionsTracker.definitions)
                                                                                .sort(([, a], [, b]) => a.PGN - b.PGN)
                                                                                .map(([id, definition], index) => {
                                                                                const isEvenRow = index % 2 === 0;
                                                                                return ((0, jsx_runtime_1.jsxs)("tr", { style: {
                                                                                        backgroundColor: selectedDefinitionId === id
                                                                                            ? '#d1ecf1'
                                                                                            : isEvenRow
                                                                                                ? '#ffffff'
                                                                                                : 'rgba(0,0,0,.05)',
                                                                                        cursor: 'pointer',
                                                                                    }, onClick: () => handleDefinitionSelect(id, definition), children: [(0, jsx_runtime_1.jsx)("td", { style: { color: 'red', fontWeight: 'bold' }, children: definition.PGN }), (0, jsx_runtime_1.jsx)("td", { style: { fontFamily: 'monospace' }, children: definition.Description }), (0, jsx_runtime_1.jsx)("td", { style: { textAlign: 'center' }, children: (0, jsx_runtime_1.jsx)(reactstrap_1.Button, { color: "danger", size: "sm", outline: true, onClick: (e) => {
                                                                                                    e.stopPropagation();
                                                                                                    clearSingleDefinition(id);
                                                                                                }, title: "Remove from changed list", style: { lineHeight: 1, padding: '0.125rem 0.375rem' }, children: "\u00D7" }) })] }, id));
                                                                            }) })] }) }))] }) }) }) }), (0, jsx_runtime_1.jsxs)(reactstrap_1.Row, { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.Col, { md: "6", children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { style: { height: 'calc(100vh - 400px)', minHeight: '800px' }, children: (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { style: { height: '100%', overflow: 'hidden', padding: 0 }, children: selectedDefinitionId && currentPgn ? ((0, jsx_runtime_1.jsx)(PgnDefinitionTab_1.PgnDefinitionTab, { definition: exports.changedDefinitionsTracker.getDefinition(selectedDefinitionId), pgnData: currentPgn, onSave: handleDefinitionSave, hasBeenChanged: true, changedLookups: exports.changedDefinitionsTracker.getChangedLookups() })) : ((0, jsx_runtime_1.jsxs)("div", { className: "text-center text-muted p-4", style: { height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }, children: [(0, jsx_runtime_1.jsx)("h5", { children: "PGN Definition Structure" }), (0, jsx_runtime_1.jsx)("p", { children: "Select a PGN definition from the list above to view its field structure and properties here." }), (0, jsx_runtime_1.jsxs)("div", { className: "text-start", children: [(0, jsx_runtime_1.jsx)("h6", { children: "This panel will show:" }), (0, jsx_runtime_1.jsxs)("ul", { className: "list-unstyled", children: [(0, jsx_runtime_1.jsx)("li", { children: "\u2713 Field definitions and properties" }), (0, jsx_runtime_1.jsx)("li", { children: "\u2713 Data types and units" }), (0, jsx_runtime_1.jsx)("li", { children: "\u2713 Enumeration mappings" }), (0, jsx_runtime_1.jsx)("li", { children: "\u2713 Field editing capabilities" })] })] })] })) }) }) }), (0, jsx_runtime_1.jsx)(reactstrap_1.Col, { md: "6", children: (0, jsx_runtime_1.jsx)(reactstrap_1.Card, { style: { height: 'calc(100vh - 400px)', minHeight: '800px' }, children: (0, jsx_runtime_1.jsx)(reactstrap_1.CardBody, { style: { height: '100%', overflow: 'hidden', padding: 0 }, children: selectedDefinitionId ? ((0, jsx_runtime_1.jsx)(SentencePanel_1.SentencePanel, { selectedPgn: selectedPgn, selectedPgnWithHistory: selectedPgnWithHistory, definition: exports.changedDefinitionsTracker.getDefinition(selectedDefinitionId), onDefinitionSave: handleDefinitionSave, info: deviceInfo || new rxjs_1.ReplaySubject(), inEditingTab: true })) : ((0, jsx_runtime_1.jsxs)("div", { className: "text-center text-muted p-4", style: { height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }, children: [(0, jsx_runtime_1.jsx)("h5", { children: "PGN Data Analysis" }), (0, jsx_runtime_1.jsx)("p", { children: "Select a PGN definition from the list above to view parsed data and analysis here." }), (0, jsx_runtime_1.jsxs)("div", { className: "text-start", children: [(0, jsx_runtime_1.jsx)("h6", { children: "This panel will show:" }), (0, jsx_runtime_1.jsxs)("ul", { className: "list-unstyled", children: [(0, jsx_runtime_1.jsx)("li", { children: "\u2713 Parsed PGN data values" }), (0, jsx_runtime_1.jsx)("li", { children: "\u2713 Raw data interpretation" }), (0, jsx_runtime_1.jsx)("li", { children: "\u2713 Field-by-field analysis" }), (0, jsx_runtime_1.jsx)("li", { children: "\u2713 Data validation results" })] })] })] })) }) }) })] })] }), (0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: LOOKUPS_TAB, children: (0, jsx_runtime_1.jsx)(LookupEditor_1.default, { changedLookups: exports.changedDefinitionsTracker.lookups, onLookupChange: handleLookupChange, onLookupRemove: handleLookupRemove, onClearAll: clearAllLookups, onLookupEdit: handleLookupEdit }, `lookups-${trackerVersion}`) }), (0, jsx_runtime_1.jsx)(reactstrap_1.TabPane, { tabId: BIT_LOOKUPS_TAB, children: (0, jsx_runtime_1.jsx)(BitLookupEditor_1.default, { changedBitLookups: exports.changedDefinitionsTracker.bitLookups, onBitLookupChange: handleBitLookupChange, onBitLookupRemove: handleBitLookupRemove, onClearAll: clearAllBitLookups, onBitLookupEdit: handleLookupEdit }, `bitlookups-${trackerVersion}`) })] })] }) }), (0, jsx_runtime_1.jsxs)(reactstrap_1.Modal, { isOpen: !!editingLookup, toggle: closeLookupEditor, size: "lg", children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.ModalHeader, { toggle: closeLookupEditor, children: [editingLookup?.enumName ? 'Edit' : 'Create', " ", editingLookup?.type === 'lookup' ? 'Lookup' : 'Bit Lookup', ' ', "Enumeration", editingLookup?.enumName && editingLookup.enumName === newLookupName && ((0, jsx_runtime_1.jsxs)("div", { className: "small text-muted", children: ["Current lookup: ", editingLookup.enumName] }))] }), (0, jsx_runtime_1.jsxs)(reactstrap_1.ModalBody, { children: [editingLookup && ((0, jsx_runtime_1.jsxs)("div", { className: "mb-3", children: [(0, jsx_runtime_1.jsxs)("label", { className: "form-label", children: [editingLookup.type === 'lookup' ? 'Lookup' : 'Bit Lookup', " Name"] }), (0, jsx_runtime_1.jsx)(reactstrap_1.Input, { type: "text", placeholder: `Enter ${editingLookup.type === 'lookup' ? 'lookup' : 'bit lookup'} name`, value: newLookupName, onChange: (e) => setNewLookupName(e.target.value) }), editingLookup.enumName && editingLookup.enumName !== newLookupName && ((0, jsx_runtime_1.jsxs)("div", { className: "small text-muted mt-1", children: ["Original name: ", editingLookup.enumName] }))] })), (0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [(0, jsx_runtime_1.jsx)("h6", { children: "Lookup Values" }), (0, jsx_runtime_1.jsxs)(reactstrap_1.Button, { color: "success", size: "sm", onClick: addLookupValue, children: [(0, jsx_runtime_1.jsx)("i", { className: "fa fa-plus me-2" }), "Add Value"] })] }), (0, jsx_runtime_1.jsx)("div", { className: "table-responsive", style: { maxHeight: '400px', overflowY: 'auto' }, children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Table, { size: "sm", bordered: true, children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("th", { style: { width: '120px' }, children: "Key/Value" }), (0, jsx_runtime_1.jsx)("th", { children: "Name/Description" }), (0, jsx_runtime_1.jsx)("th", { style: { width: '80px' }, children: "Actions" })] }) }), (0, jsx_runtime_1.jsx)("tbody", { children: lookupValues.map((item, index) => ((0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: (0, jsx_runtime_1.jsx)(reactstrap_1.Input, { type: editingLookup?.type === 'bitlookup' ? 'text' : 'number', size: "sm", value: item.key, onChange: (e) => updateLookupValue(index, e.target.value, item.value), placeholder: editingLookup?.type === 'bitlookup' ? '0x01' : '0' }) }), (0, jsx_runtime_1.jsx)("td", { children: (0, jsx_runtime_1.jsx)(reactstrap_1.Input, { type: "text", size: "sm", value: item.value, onChange: (e) => updateLookupValue(index, item.key, e.target.value), placeholder: "Value name" }) }), (0, jsx_runtime_1.jsx)("td", { className: "text-center", children: (0, jsx_runtime_1.jsx)(reactstrap_1.Button, { color: "danger", size: "sm", onClick: () => removeLookupValue(index), title: "Remove value", children: (0, jsx_runtime_1.jsx)("i", { className: "fa fa-trash" }) }) })] }, index))) })] }) }), lookupValues.length === 0 && ((0, jsx_runtime_1.jsxs)("div", { className: "text-center text-muted p-4", children: [(0, jsx_runtime_1.jsx)("i", { className: "fa fa-info-circle me-2" }), "No lookup values defined. Click \"Add Value\" to start."] }))] }), (0, jsx_runtime_1.jsxs)(reactstrap_1.ModalFooter, { children: [(0, jsx_runtime_1.jsxs)(reactstrap_1.Button, { color: "primary", onClick: saveLookupValues, children: [(0, jsx_runtime_1.jsx)("i", { className: "fa fa-save me-2" }), "Save Lookup"] }), (0, jsx_runtime_1.jsx)(reactstrap_1.Button, { color: "secondary", onClick: closeLookupEditor, children: "Cancel" })] })] })] }));
};
exports.default = EditorTab;
